// The paket intake from an LPSE announcement (B6), in two steps, shared by
// Buat project (./PaketCreateDialog.tsx) and the paket page (Tempel
// Pengumuman LPSE in Persiapan, PaketIntakeDialog below):
//   input     Tempel Pengumuman LPSE (required) and Unggah dokumen
//             pemilihan (optional, 1-5 files, stored as dokumen_pemilihan)
//   read      POST /projects/:id/intake: Pluto reads, nothing is saved
//   confirm   the fields Pluto read, each editable with the text it came
//             from as its hint, the details it found, and the requirements
//             (each removable); Simpan saves (POST /intake/confirm), Lewati
//             leaves the paket as it is
// A read that finds nothing (proposal null) says so; an error keeps the
// pasted text so it can be read again.
import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import * as API from "../api";
import type { JenisPengadaan, Kualifikasi, MetodePemilihan, PaketIntakeFields, PaketIntakeResult, ProjectDetail } from "../api";
import { formatCount } from "../lib/format";
import { checkProjectFiles } from "../project/files";
import { hpsDigits, isLink } from "../project/PaketEditDialog";
import { JadwalTable, StageSuggestion } from "../project/PaketJadwal";
import { JENIS_LABEL, KUALIFIKASI_LABEL, METODE_LABEL } from "../project/stages";
import { Dialog, Field, Notice } from "../ui/components";
import { Glyph } from "../ui/icons";

/** API limit of the pasted announcement. */
export const ANNOUNCEMENT_MAX = 30_000;
/** The intake reads PDF, DOCX, XLSX and TXT. */
export const INTAKE_ACCEPT = ".pdf,.docx,.xlsx,.txt";

export const NOTHING_FOUND = "Pluto tidak menemukan data paket di pengumuman ini";

/** The message of a failed intake call, named for the person. */
export function intakeErrorMessage(err: unknown): string {
  if (err instanceof API.ApiError) {
    if (err.status === 403) return "Pengisian dari pengumuman hanya untuk project paket akun Penyedia.";
    if (err.status === 429) return "Batas penggunaan Pluto untuk saat ini tercapai. Coba lagi beberapa saat lagi.";
    if (err.status === 502) return "Pluto sedang sibuk dan belum dapat membaca pengumuman. Coba lagi sebentar lagi.";
    if (err.status === 503 && err.body?.code === "drive_not_connected") return "Penyimpanan file belum terhubung, jadi dokumen pemilihan belum bisa diunggah. Baca pengumuman tanpa file, atau hubungi admin.";
  }
  return API.errorMessage(err);
}

// ---------------------------------------------------------------------------
// Step 1: the announcement and the optional dokumen pemilihan
// ---------------------------------------------------------------------------
export interface AnnouncementInputProps {
  idBase: string;
  announcement: string;
  onAnnouncement: (text: string) => void;
  files: File[];
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  /** Shown under the textarea (e.g. empty_announcement). */
  error?: string | null;
  autoFocus?: boolean;
}

export function AnnouncementInput({ idBase, announcement, onAnnouncement, files, onFiles, disabled, error, autoFocus }: AnnouncementInputProps) {
  const picker = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const textId = `${idBase}-announcement`;
  const fileId = `${idBase}-files`;

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.currentTarget.files ?? []);
    e.currentTarget.value = "";
    if (picked.length === 0) return;
    const next = [...files, ...picked];
    const problem = checkProjectFiles(next);
    setFileError(problem);
    if (!problem) onFiles(next);
  };

  return (
    <>
      <Field id={textId} label="Tempel Pengumuman LPSE" required error={error} hint="Salin seluruh halaman pengumuman paket dari LPSE: nama paket, satuan kerja, HPS, jadwal, dan persyaratan.">
        <textarea
          id={textId}
          rows={6}
          maxLength={ANNOUNCEMENT_MAX}
          value={announcement}
          readOnly={disabled}
          required
          data-autofocus={autoFocus ? "" : undefined}
          aria-describedby={`${textId}-hint`}
          aria-invalid={error ? true : undefined}
          placeholder={"Kode Tender 90122\nNama Paket : ...\nSatuan Kerja : ...\nNilai HPS Paket : Rp ..."}
          onChange={(e) => onAnnouncement(e.currentTarget.value)}
        />
      </Field>
      <Field id={fileId} label="Unggah dokumen pemilihan" error={fileError} hint="Opsional. PDF, DOCX, XLSX, atau TXT, paling banyak 5 file, masing-masing 10 MB.">
        <div className="pyi-files">
          <input ref={picker} id={fileId} type="file" multiple accept={INTAKE_ACCEPT} hidden disabled={disabled} onChange={onPick} aria-describedby={`${fileId}-hint`} />
          <button type="button" className="btn btn-secondary" disabled={disabled || files.length >= 5} onClick={() => picker.current?.click()}>
            <Glyph name="paperclip" size={18} />
            <span>Pilih file</span>
          </button>
          {files.length > 0 ? (
            <ul className="pyi-file-list" aria-label="Dokumen pemilihan dipilih">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="pyi-file">
                  <Glyph name="documentText" size={16} />
                  <span className="pyi-file-name">{f.name}</span>
                  <button
                    type="button"
                    className="ui-icon-btn"
                    aria-label={`Batalkan ${f.name}`}
                    title="Batalkan"
                    disabled={disabled}
                    onClick={() => onFiles(files.filter((_, n) => n !== i))}
                  >
                    <Glyph name="close" size={16} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Field>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 2: confirm what Pluto read
// ---------------------------------------------------------------------------
type TextKey = "paketName" | "instansi" | "kodePaket" | "kodeRup" | "lpse" | "lokasi" | "sumberUrl" | "statusPaket" | "ringkasanKak";
type MoneyKey = "nilaiHps" | "pagu";
type Key = TextKey | MoneyKey | "deadline" | "deadlineTime" | "durasiHari" | "jenisPengadaan" | "metode" | "kualifikasi";

const LABEL: Record<Key, string> = {
  paketName: "Nama paket",
  instansi: "Instansi",
  nilaiHps: "Nilai HPS (Rp)",
  deadline: "Batas penawaran",
  sumberUrl: "Tautan LPSE",
  kodePaket: "Kode paket",
  kodeRup: "Kode RUP",
  lpse: "LPSE",
  lokasi: "Lokasi",
  jenisPengadaan: "Jenis pengadaan",
  metode: "Metode pemilihan",
  kualifikasi: "Kualifikasi",
  pagu: "Pagu (Rp)",
  deadlineTime: "Jam batas penawaran",
  durasiHari: "Durasi pelaksanaan (hari)",
  statusPaket: "Status paket",
  ringkasanKak: "Ringkasan KAK",
};

/** Always shown (the paket's identity); the rest only when Pluto read something for them. */
const MAIN: Key[] = ["paketName", "instansi", "nilaiHps", "deadline", "sumberUrl"];
const DETAIL: Key[] = ["kodePaket", "kodeRup", "lpse", "lokasi", "jenisPengadaan", "metode", "kualifikasi", "pagu", "deadlineTime", "durasiHari", "statusPaket", "ringkasanKak"];
const ALL: Key[] = [...MAIN, ...DETAIL];

const MAX: Partial<Record<Key, number>> = { paketName: 300, instansi: 200, kodePaket: 100, kodeRup: 100, lpse: 200, lokasi: 200, sumberUrl: 500, statusPaket: 100, ringkasanKak: 2000 };

const KIND_LABEL: Record<string, string> = {
  kbli: "KBLI",
  sbu: "SBU",
  pengalaman: "Pengalaman",
  personel: "Personel",
  peralatan: "Peralatan",
  jaminan: "Jaminan",
  lainnya: "Lainnya",
};

type Form = Record<Key, string>;

function formOf(fields: PaketIntakeFields): Form {
  const out = {} as Form;
  for (const key of ALL) {
    const v = (fields as Record<string, unknown>)[key];
    out[key] = v === null || v === undefined ? "" : typeof v === "number" && (key === "nilaiHps" || key === "pagu") ? String(Math.round(v)) : String(v);
  }
  return out;
}

/** The confirmed fields: an empty value is left out (the API keeps the saved one). */
export function fieldsOf(form: Form, requirements: string[]): PaketIntakeFields {
  const out: Record<string, unknown> = {};
  for (const key of ALL) {
    const v = form[key].trim();
    if (!v) continue;
    if (key === "nilaiHps" || key === "pagu") out[key] = Number(v);
    else if (key === "durasiHari") {
      const n = Number(v.replace(/[^\d]/g, ""));
      if (Number.isInteger(n) && n >= 1) out[key] = n;
    } else out[key] = v;
  }
  if (requirements.length > 0) out.requirements = requirements;
  return out as PaketIntakeFields;
}

/** The text Pluto read a field from, or "" (an older proposal may lack the detail fields). */
function rawOf(result: PaketIntakeResult, key: Key): string {
  const field = (result.proposal as Record<string, { raw?: string } | undefined> | null)?.[key];
  return field?.raw?.trim() ?? "";
}

export interface IntakeConfirmProps {
  /** The form's id, for the submit button in a dialog footer. */
  formId: string;
  result: PaketIntakeResult;
  busy: boolean;
  onSubmit: (fields: PaketIntakeFields) => void;
}

/** What Pluto read, editable: the source text as each field's hint; the requirements, each removable. */
export function IntakeConfirm({ formId, result, busy, onSubmit }: IntakeConfirmProps) {
  const id = useId();
  const [form, setForm] = useState<Form>(() => formOf(result.fields ?? {}));
  const reqs0 = result.proposal?.requirements ?? (result.fields?.requirements ?? []).map((text) => ({ kind: "lainnya", text }));
  const [reqs, setReqs] = useState(() => reqs0.map((r, i) => ({ key: `r-${i}`, kind: r.kind as string, text: r.text })));
  const jadwal = result.proposal?.jadwal ?? result.fields?.jadwal ?? [];
  const suggestion = result.proposal?.stageSuggestion;
  // The schedule's forward move, offered ticked; the API ignores it for a paket already there.
  const [move, setMove] = useState(suggestion?.value === "pelaksanaan_pemilihan");
  useEffect(() => {
    setForm(formOf(result.fields ?? {}));
    setMove(result.proposal?.stageSuggestion?.value === "pelaksanaan_pemilihan");
  }, [result]);

  const set = (key: Key, value: string) => setForm((f) => ({ ...f, [key]: value }));
  const linkBad = form.sumberUrl.trim() !== "" && !isLink(form.sumberUrl.trim());
  const shown = ALL.filter((k) => MAIN.includes(k) || form[k] !== "" || rawOf(result, k) !== "");
  const details = shown.filter((k) => !MAIN.includes(k));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || linkBad) return;
    onSubmit({
      ...fieldsOf(form, reqs.map((r) => r.text)),
      ...(jadwal.length > 0 ? { jadwal } : {}),
      ...(move && suggestion?.value === "pelaksanaan_pemilihan" ? { moveTo: "pelaksanaan_pemilihan" as const } : {}),
    });
  };

  const control = (key: Key, fid: string): ReactNode => {
    const common = { id: fid, readOnly: busy, "aria-describedby": `${fid}-hint` } as const;
    if (key === "nilaiHps" || key === "pagu") {
      return <input {...common} inputMode="numeric" placeholder="mis. 1.250.000.000" value={form[key] ? formatCount(Number(form[key])) : ""} onChange={(e) => set(key, hpsDigits(e.currentTarget.value))} />;
    }
    if (key === "deadline") return <input {...common} type="date" value={form.deadline} onChange={(e) => set(key, e.currentTarget.value)} />;
    if (key === "deadlineTime") return <input {...common} type="time" value={form.deadlineTime} onChange={(e) => set(key, e.currentTarget.value)} />;
    if (key === "durasiHari") return <input {...common} inputMode="numeric" value={form.durasiHari} onChange={(e) => set(key, e.currentTarget.value.replace(/[^\d]/g, "").slice(0, 4))} />;
    if (key === "jenisPengadaan" || key === "metode" || key === "kualifikasi") {
      const labels: Record<string, string> = key === "jenisPengadaan" ? JENIS_LABEL : key === "metode" ? METODE_LABEL : KUALIFIKASI_LABEL;
      return (
        <select id={fid} disabled={busy} aria-describedby={`${fid}-hint`} value={form[key]} onChange={(e) => set(key, e.currentTarget.value as JenisPengadaan | MetodePemilihan | Kualifikasi | "")}>
          <option value="">Belum diisi</option>
          {Object.entries(labels).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      );
    }
    if (key === "ringkasanKak") return <textarea id={fid} rows={3} maxLength={MAX[key]} readOnly={busy} aria-describedby={`${fid}-hint`} value={form[key]} onChange={(e) => set(key, e.currentTarget.value)} />;
    return <input {...common} type={key === "sumberUrl" ? "url" : "text"} maxLength={MAX[key]} value={form[key]} onChange={(e) => set(key, e.currentTarget.value)} />;
  };

  const field = (key: Key) => {
    const fid = `${id}-${key}`;
    const raw = rawOf(result, key);
    const empty = form[key].trim() === "";
    const missing = MAIN.includes(key) && empty;
    const hint = raw ? `Dari teks: "${raw}"` : missing ? "Belum ditemukan. Isi bila sudah tahu." : undefined;
    return (
      <div key={key} className="ppc-field" data-missing={missing ? "" : undefined} data-key={key}>
        <Field id={fid} label={LABEL[key]} hint={hint} error={key === "sumberUrl" && linkBad ? "Tautan harus diawali http:// atau https://" : null}>
          {control(key, fid)}
        </Field>
      </div>
    );
  };

  return (
    <form id={formId} className="pyi-confirm" onSubmit={submit} noValidate aria-label="Periksa info paket">
      <p className="dialog-text">Periksa data yang Pluto baca dari pengumuman. Ubah bila perlu; kolom yang dikosongkan tidak mengubah data project.</p>
      <div className="ppc-fields">{MAIN.map(field)}</div>
      {details.length > 0 ? (
        <div className="ppc-extras">
          <h4 className="ppc-extras-title">Rincian pengumuman</h4>
          <div className="ppc-fields">{details.map(field)}</div>
        </div>
      ) : null}
      {jadwal.length > 0 ? (
        <div className="ppc-extras">
          <JadwalTable jadwal={jadwal} />
        </div>
      ) : null}
      <StageSuggestion suggestion={suggestion} checked={move} disabled={busy} onChange={setMove} />
      <div className="ppc-reqs">
        <h4 className="ppc-reqs-title">Persyaratan untuk checklist Pelaksanaan Pemilihan</h4>
        {reqs.length === 0 ? (
          <p className="pks-caption">Tidak ada persyaratan yang ditambahkan.</p>
        ) : (
          <ul className="ppc-req-list" aria-label="Persyaratan yang ditemukan">
            {reqs.map((r) => (
              <li key={r.key} className="ppc-req pyi-req">
                <span className="ppc-req-text">{r.text}</span>
                <span className="ppc-req-kind">{KIND_LABEL[r.kind] ?? r.kind}</span>
                <button type="button" className="ui-icon-btn" aria-label={`Hapus persyaratan ${r.text}`} title="Hapus" disabled={busy} onClick={() => setReqs((list) => list.filter((x) => x.key !== r.key))}>
                  <Glyph name="trash" size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {result.files.length > 0 ? (
        <p className="pks-caption">
          {result.files.length} dokumen pemilihan tersimpan di project: {result.files.map((f) => f.name).join(", ")}.
        </p>
      ) : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// The intake on the paket page
// ---------------------------------------------------------------------------
type Phase = { at: "input" } | { at: "confirm"; result: PaketIntakeResult } | { at: "empty"; result: PaketIntakeResult };

export interface PaketIntakeDialogProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  /** The confirmed paket (project and checklist). */
  onSaved: (detail: ProjectDetail, added: number) => void;
  /** Files were stored as dokumen pemilihan (the document list reloads). */
  onFiles?: () => void;
}

/** Tempel Pengumuman LPSE on an existing paket: read, then confirm. */
export function PaketIntakeDialog({ open, projectId, onClose, onSaved, onFiles }: PaketIntakeDialogProps) {
  const id = useId();
  const formId = `${id}-confirm`;
  const [phase, setPhase] = useState<Phase>({ at: "input" });
  const [announcement, setAnnouncement] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPhase({ at: "input" });
      setAnnouncement("");
      setFiles([]);
      setError(null);
    }
  }, [open]);

  const read = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy || !announcement.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await API.intakePaket(projectId, announcement.trim(), files);
      if (result.files.length > 0) {
        setFiles([]);
        onFiles?.();
      }
      setPhase(result.proposal && result.fields ? { at: "confirm", result } : { at: "empty", result });
    } catch (err) {
      setError(intakeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const save = async (fields: PaketIntakeFields) => {
    setBusy(true);
    setError(null);
    try {
      const saved = await API.confirmPaketIntake(projectId, fields);
      onSaved({ project: saved.project, checklist: saved.checklist }, saved.added);
    } catch (err) {
      setError(intakeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const inputId = `${id}-input`;
  let body: ReactNode;
  let footer: ReactNode;
  if (phase.at === "confirm") {
    body = <IntakeConfirm formId={formId} result={phase.result} busy={busy} onSubmit={(f) => void save(f)} />;
    footer = (
      <>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
          Lewati
        </button>
        <button type="submit" form={formId} className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
          {busy ? "Menyimpan" : "Simpan"}
        </button>
      </>
    );
  } else {
    body = (
      <form id={inputId} className="pj-form" onSubmit={(e) => void read(e)} noValidate>
        {phase.at === "empty" ? (
          <Notice tone="warning" title={NOTHING_FOUND}>
            <p>Periksa apakah teks yang ditempel sudah memuat nama paket, satuan kerja, dan HPS, lalu baca ulang. Data paket juga bisa diisi lewat Lengkapi Data.</p>
          </Notice>
        ) : null}
        <AnnouncementInput idBase={id} announcement={announcement} onAnnouncement={setAnnouncement} files={files} onFiles={setFiles} disabled={busy} autoFocus />
      </form>
    );
    footer = (
      <button type="submit" form={inputId} className="btn btn-primary" disabled={busy || !announcement.trim()} aria-busy={busy || undefined}>
        <Glyph name="magicStar" size={18} />
        <span>{busy ? "Pluto membaca" : phase.at === "empty" ? "Baca ulang" : "Baca pengumuman"}</span>
      </button>
    );
  }

  return (
    <Dialog open={open} title={phase.at === "confirm" ? "Periksa info paket" : "Tempel Pengumuman LPSE"} size="md" busy={busy} onClose={onClose} footer={footer}>
      <div className="pyi">
        {body}
        {error ? (
          <Notice tone="danger" title={phase.at === "confirm" ? "Info paket belum tersimpan" : "Pengumuman belum terbaca"}>
            <p>{error}</p>
          </Notice>
        ) : null}
      </div>
    </Dialog>
  );
}
